import React, { useState } from 'react'
import { useAppDispatch } from '../../redux/hooks'
import { deleteMoodEntry, updateMoodEntry } from '../../redux/mood/moodSlice'
import type { MoodEntryDto } from '../../Models/Mood'
import { TagInput, TagList } from '../TagInput'
import {
  formatLoggedTime,
  getCheckInTitle,
  MoodScoreFace,
} from '../MoodSection/MoodSection'
import styles from '../MoodSection/MoodSection.module.css'

const SCORE_NUMBERS = Array.from({ length: 10 }, (_, i) => i + 1)

export type MoodEntryCardProps = {
  entry: MoodEntryDto
  /** Calendar day key used for `byDate` cache updates (usually `entry.loggedDate`). */
  dateKey: string
}

const MoodEntryCard = ({ entry: e, dateKey }: MoodEntryCardProps) => {
  const dispatch = useAppDispatch()
  const [editing, setEditing] = useState(false)
  const [editScore, setEditScore] = useState(e.score)
  const [editNotes, setEditNotes] = useState(e.notes ?? '')
  const [editPositiveTags, setEditPositiveTags] = useState<string[]>([...(e.positiveTags ?? [])])
  const [editNegativeTags, setEditNegativeTags] = useState<string[]>([...(e.negativeTags ?? [])])
  const [editSaving, setEditSaving] = useState(false)

  const startEdit = () => {
    setEditing(true)
    setEditScore(e.score)
    setEditNotes(e.notes ?? '')
    setEditPositiveTags([...(e.positiveTags ?? [])])
    setEditNegativeTags([...(e.negativeTags ?? [])])
  }

  const cancelEdit = () => {
    setEditing(false)
  }

  const onSaveEdit = async () => {
    setEditSaving(true)
    try {
      await dispatch(
        updateMoodEntry({
          id: e.id,
          dateKey,
          patch: {
            score: editScore,
            notes: editNotes.trim() || null,
            positiveTags: editPositiveTags.length ? editPositiveTags : null,
            negativeTags: editNegativeTags.length ? editNegativeTags : null,
          },
        })
      ).unwrap()
      setEditing(false)
    } catch {
      /* handled in store */
    } finally {
      setEditSaving(false)
    }
  }

  const onDelete = () => {
    if (!window.confirm('Remove this mood entry?')) return
    void dispatch(deleteMoodEntry({ id: e.id, dateKey }))
  }

  const hasPositive = (e.positiveTags?.length ?? 0) > 0
  const hasNegative = (e.negativeTags?.length ?? 0) > 0

  return (
    <article className={styles.logCard}>
      {editing ? (
        <div className={styles.editFormCard}>
          <div className={styles.sliderSection} aria-label="Mood score">
            <div className={styles.emojiRow}>
              {SCORE_NUMBERS.map((n) => (
                <button
                  key={n}
                  type="button"
                  className={`${styles.emojiIcon} ${editScore === n ? styles.emojiActive : ''}`}
                  aria-pressed={editScore === n}
                  onClick={() => setEditScore(n)}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>

          <div className={styles.fieldGroup}>
            <label htmlFor={`edit-notes-${e.id}`} className={styles.fieldLabel}>
              Notes
            </label>
            <textarea
              id={`edit-notes-${e.id}`}
              className={styles.textarea}
              value={editNotes}
              onChange={(ev) => setEditNotes(ev.target.value)}
              placeholder="Anything on your mind..."
              rows={4}
            />
          </div>

          <div className={styles.fieldGroup}>
            <span id={`edit-pos-label-${e.id}`} className={styles.fieldLabel}>
              What helped
            </span>
            <div className={styles.tagInputWrapper}>
              <TagInput
                id={`edit-pos-${e.id}`}
                tags={editPositiveTags}
                onChange={setEditPositiveTags}
                tone="positive"
                disabled={editSaving}
                placeholderEmpty="Add tag"
                placeholderMore="Add tag"
                aria-labelledby={`edit-pos-label-${e.id}`}
              />
            </div>
          </div>

          <div className={styles.fieldGroup}>
            <span id={`edit-neg-label-${e.id}`} className={styles.fieldLabel}>
              What was tough
            </span>
            <div className={styles.tagInputWrapper}>
              <TagInput
                id={`edit-neg-${e.id}`}
                tags={editNegativeTags}
                onChange={setEditNegativeTags}
                tone="negative"
                disabled={editSaving}
                placeholderEmpty="Add tag"
                placeholderMore="Add tag"
                aria-labelledby={`edit-neg-label-${e.id}`}
              />
            </div>
          </div>

          <div className={`${styles.submitRow} ${styles.submitRowSpaced}`}>
            <button
              type="button"
              className={styles.submitBtn}
              disabled={editSaving}
              onClick={() => void onSaveEdit()}
            >
              {editSaving ? 'Saving…' : 'Save changes'}
            </button>
            <button type="button" className={styles.cancelBtn} onClick={cancelEdit}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className={styles.logCardHeader}>
            <div className={styles.logCardLeft}>
              <span className={styles.logCardEmoji}>
                <MoodScoreFace score={e.score} />
              </span>
              <div className={styles.logCardMeta}>
                <span className={styles.logCardTitle}>{getCheckInTitle(e.loggedAt)}</span>
                <span className={styles.logCardTime}>{formatLoggedTime(e.loggedAt)}</span>
              </div>
            </div>
            <span className={styles.logCardScore} aria-label={`Mood score ${e.score}`}>
              {e.score}
            </span>
          </div>

          {e.notes ? <p className={styles.logCardNotesHistory}>{e.notes}</p> : null}

          {hasPositive ? (
            <div className={styles.logCardDetailBlock}>
              <span className={styles.logCardDetailLabel}>What helped</span>
              <div className={styles.logCardTagRow}>
                <TagList tags={e.positiveTags ?? []} tone="positive" />
              </div>
            </div>
          ) : null}

          {hasNegative ? (
            <div className={styles.logCardDetailBlock}>
              <span className={styles.logCardDetailLabel}>What was tough</span>
              <div className={styles.logCardTagRow}>
                <TagList tags={e.negativeTags ?? []} tone="negative" />
              </div>
            </div>
          ) : null}

          <div className={styles.logCardToolbar}>
            <button type="button" className={styles.logCardActionBtn} onClick={startEdit}>
              Edit
            </button>
            <button
              type="button"
              className={`${styles.logCardActionBtn} ${styles.logCardActionBtnDanger}`}
              onClick={onDelete}
            >
              Delete
            </button>
          </div>
        </>
      )}
    </article>
  )
}

export default MoodEntryCard
